import getClientApiRequestTransformer from '@/api/transformers/auth.request.transformer';
import getSocketIdRequestTransformer from '@/api/transformers/socketId.request.transformer';
import getClientsTransformer from '@/api/transformers/clients/clientsTransformer';
import TakeAway from '@/models/takeAway';
import getReceiptsTransformer from './transformers/pos/receiptsTransformer';
import getDoneAndSavedReceiptsTransformer from './transformers/pos/doneAndSavedReceiptsTransformer';
import getActualOrdersTransformer from './transformers/pos/actualOrdersTransformer';
import { PaymentMethods } from '@/interfaces/enums';
import axios, { AxiosResponse } from 'axios';
import apiClient from '@/apiClient';

export default {
  async fetchActualOrders() {
    return window.axios.get('/pos/actual/orders', {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getActualOrdersTransformer()
    });
  },
  async fetchActualOrdersForTV(placeId: string) {
    return axios.get(`/tv/order/list/${placeId}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    })
      .then(({ data }: AxiosResponse) => data);
  },
  async fetchReceipts() {
    return window.axios.get('/pos/receipt/list', {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getDoneAndSavedReceiptsTransformer()
    });
  },
  async fetchNextReceipts(receiptsCount: number) {
    return window.axios.get(`/pos/done/orders/${receiptsCount}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getReceiptsTransformer()
    });
  },
  async createReceipt(payload: any) {
    return apiClient.post('/pos/receipt/create', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    })
      .then(({ data }: AxiosResponse<any>) => data);
  },
  async createDeliveryReceipt(payload: any) {
    return apiClient.post('/pos/delivery/create', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    })
      .then(({ data }: AxiosResponse<any>) => data);
  },
  async createOrderWithoutPayment(payload: any) {
    return apiClient.post('/pos/order/create', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    })
      .then(({ data }: AxiosResponse<any>) => data);
  },
  async repeatCreateReceipt({ id, ...payload }: any) {
    return window.axios.post(`/pos/repeate/receipt/create/${id}`, payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async createSavedReceipt(payload: any) {
    return window.axios.post('/pos/order/save', payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async fundsManipulation(cash: number, posId: string | null) {
    return window.axios.post('/pos/insertion-withdrawal', { cash, posId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async addClientToOrder({ client, id }: any) {
    return window.axios.patch(`/order/add-client/${id}`, {
      contact: client.name,
      phone: client.phone
    }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async makeZReportWithWithdrawal(posId: string | null) {
    return window.axios.post('/pos/create/report/0', { posId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async makeZReportWithoutWithdrawal(posId: string | null) {
    return window.axios.post('/pos/create/report/10', { posId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async openShift(cash: number, posId: string | null) {
    return window.axios.post('/pos/shift/open',
      {
        cash,
        posId
      },
      {
        transformRequest: [
          ...getClientApiRequestTransformer,
          ...getSocketIdRequestTransformer,
        ],
      },
      );
  },
  async createFreeReceiptByOrder(id: string, posId: string | null) {
    return this.createReceiptBySavedReceipt({
      id,
      posId,
      clientCash: 0,
      typeOfPayment: PaymentMethods.FREE
    });
  },
  async createReceiptBySavedReceipt({ id, ...payload }: any) {
    return apiClient.post(`/pos/receipt-by-order/create/${id}`, payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    })
      .then(({ data }: AxiosResponse<any>) => data);
  },
  async removeClientFromOrder(orderId: string) {
    return window.axios.delete(`/order/remove-client/${orderId}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async issueAndPay({ id, ...payload }: any) {
    return apiClient.post(`/pos/order/issue-and-pay/${id}`, payload, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    })
      .then(({ data }: AxiosResponse<any>) => data);
  },
  async refundOrder({ id, products, posId }: { id: string, products: Record<string, { count: number, gtins: string[] }>, posId: string | null }) {
    return window.axios.patch(`/pos/refund/order/${id}`, { products, posId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async refundProducts({ id, products, posId }: { id: string, products: Record<string, { count: number, gtins: string[] }>, posId: string | null }) {
    return window.axios.patch(`/pos/refund/products/${id}`, { products, posId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async issueOrder(orderId: string, posId: string | null) {
    return window.axios.patch(`/pos/order/issue/${orderId}`, { posId }, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async searchReceiptByReceiptNumber(name: string): Promise<TakeAway[]> {
    return apiClient.get(`/pos/search/number?query=${name}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getReceiptsTransformer()
    })
      .then(({ data }: AxiosResponse<TakeAway[]>) => data);
  },
  async getPosState(posId: string | null) {
    return window.axios.get(`/pos/state?posId=${posId || ''}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async repeateTestCheckPrintIfItLast(posId: string | null) {
    return apiClient.get(`/pos/receipt/reprint/test?posId=${posId || ''}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async printAdditionalReceipt(id: string, posId: string | null) {
    return apiClient.get(`/pos/additional/receipt/create/${id}?posId=${posId || ''}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async createClient(client: any) {
    return window.axios.post('/pos/client/create', client, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
    });
  },
  async searchClientByClientCredentials({ name, phone }: any): Promise<any> {
    return window.axios.get(`/pos/clients/search?name=${name}&phone=${phone}`, {
      transformRequest: [
        ...getClientApiRequestTransformer,
        ...getSocketIdRequestTransformer,
      ],
      transformResponse: getClientsTransformer()
    });
  },
};
